import type { WebGLRenderer } from 'three';
import { releaseContext } from './meshScene';

// ONE RENDERER FOR SEVERAL VIEWS of a screen (Louis 2026-10-26), by a name the screen gives them:
// the published tutorial's four colourway tiles. Each renderer is a GPU context with its own copy
// of all it draws, and the four chairs were four copies of one shape, 184 MB on a phone that was
// killing the page for want of memory. Shared, the shape is uploaded once and each tile draws into
// a plain canvas of its own (see mountAssembly, `sharedGl`).
//
// Lent out and counted; given back to the browser when the last view returns it. A context the
// browser takes back is not lent again: the views remount (see onContextLost) and get a new one.
const pool = new Map<string, { renderer: WebGLRenderer; users: number }>();

export function borrowRenderer(name: string, make: () => WebGLRenderer): WebGLRenderer {
  let entry = pool.get(name);
  if (!entry) {
    const renderer = make();
    const made = { renderer, users: 0 };
    renderer.domElement.addEventListener('webglcontextlost', () => {
      if (pool.get(name) === made) pool.delete(name);
    });
    pool.set(name, made);
    entry = made;
  }
  entry.users += 1;
  return entry.renderer;
}

export function returnRenderer(name: string, renderer: WebGLRenderer) {
  const entry = pool.get(name);
  if (entry?.renderer !== renderer) {
    // Taken back by the browser and replaced: nobody else holds this one.
    releaseContext(renderer);
    return;
  }
  entry.users -= 1;
  if (entry.users > 0) return;
  pool.delete(name);
  releaseContext(renderer);
}
